import apiClient from './apiClient';

export const getDashboard = async () => {
  const response = await apiClient.get('/api/Admin/Dashboard');
  return response.data.data;
};

export const getHotelInfo = async () => {
  const response = await apiClient.get('/api/Hotel/GetHotel');
  return response.data.data || {};
};

// Room Types
export const getRoomTypes = async () => {
  const response = await apiClient.get('/api/Room/types');
  return response.data.data || [];
};

export const addRoomType = async (data) => {
  const response = await apiClient.post('/api/Room/AddRoomType', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add room type');
  return response.data.data; // returns new room type ID (int)
};

export const uploadRoomTypeImages = async (roomTypeId, files) => {
  const formData = new FormData();
  formData.append('MediaType', '0');
  formData.append('RoomTypeId', String(roomTypeId));
  files.forEach(file => formData.append('Files', file));
  const response = await apiClient.post('/api/File/Upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to upload images');
  return response.data.data || [];
};

export const getRoomTypeImages = async (roomTypeId) => {
  const response = await apiClient.get('/api/File/Files', { params: { mediaType: 0, roomTypeId } });
  return response.data.data || [];
};

export const deleteRoomImages = async (fileIds) => {
  await Promise.all(fileIds.map(id => apiClient.delete(`/api/File/${id}`)));
};

export const updateRoomType = async (id, data) => {
  const response = await apiClient.put(`/api/Room/type/${id}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update room type');
  return response.data.data;
};

export const setRoomDiscount = async (id, discountPercent) => {
  const r = await apiClient.patch(`/api/Room/type/${id}/Discount`, { discountPercent });
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to set discount');
};

export const getDiscountLogs = async (id) => {
  const r = await apiClient.get(`/api/Room/type/${id}/DiscountLogs`);
  return r.data.data || [];
};

export const deleteRoomType = async (id) => {
  const response = await apiClient.delete(`/api/Room/type/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to delete room type');
  return response.data.data;
};

// Physical rooms
export const addPhysicalRooms = async (data) => {
  const response = await apiClient.post('/api/Room/Add', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add rooms');
  return response.data.data;
};

// Beds
export const getBeds = async () => {
  const response = await apiClient.post('/api/Bed/GetAll', {});
  return response.data.data || [];
};

// Hotel-specific Room Facilities (Features & Amenities)
export const getRoomFacilities = async () => {
  const response = await apiClient.get('/api/HotelRoomFacility/GetAll');
  return response.data.data || [];
};

export const addRoomFacility = async (data) => {
  const response = await apiClient.post('/api/HotelRoomFacility/Add', data);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to add facility');
  return response.data.data;
};

export const updateRoomFacility = async (id, data) => {
  const response = await apiClient.put(`/api/HotelRoomFacility/${id}`, data);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to update facility');
};

export const deleteRoomFacility = async (id) => {
  const response = await apiClient.delete(`/api/HotelRoomFacility/${id}`);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to delete facility');
};

// Bookings
export const getAdminBookings = async (params = {}) => {
  const response = await apiClient.get('/api/Admin/Bookings', { params });
  return response.data.data || { items: [], totalCount: 0, page: 1, pageSize: 20 };
};

export const getTodayCounts = async () => {
  const r = await apiClient.get('/api/Admin/Bookings/TodayCounts');
  return r.data.data || { checkInPending: 0, checkOutPending: 0 };
};

export const getAdminBookingDetail = async (id) => {
  const response = await apiClient.get(`/api/Admin/Booking/${id}`);
  if (!response.data.success) throw new Error(response.data.message || 'Booking not found');
  return response.data.data;
};

export const checkIn = async (id) => {
  const response = await apiClient.post(`/api/Admin/CheckIn/${id}`);
  if (!response.data.success) throw new Error(response.data.message || 'Check-in failed');
  return response.data;
};

export const checkOut = async (id) => {
  const response = await apiClient.post(`/api/Admin/CheckOut/${id}`);
  if (!response.data.success) throw new Error(response.data.message || 'Check-out failed');
  return response.data;
};

// Confirm payment — pass amountReceived OR discountPercent (non-null takes priority on backend)
export const confirmPayment = async (id, amountReceived, manualDiscountType = null, manualDiscountValue = null) => {
  const response = await apiClient.post(`/api/Admin/ConfirmPayment/${id}`, {
    amountReceived,
    manualDiscountType,
    manualDiscountValue,
  });
  if (!response.data.success) throw new Error(response.data.message || 'Payment confirmation failed');
  return response.data.data;
};

// Room Pricing
export const getPricingByRoomType = async (roomTypeId) => {
  const response = await apiClient.get(`/api/admin/pricing/roomtype/${roomTypeId}`);
  return response.data.data || [];
};

export const addPricing = async (data) => {
  const response = await apiClient.post('/api/admin/pricing', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add pricing rule');
  return response.data.data;
};

export const updatePricing = async (id, data) => {
  const response = await apiClient.put(`/api/admin/pricing/${id}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update pricing rule');
  return response.data.data;
};

export const deletePricing = async (id) => {
  const response = await apiClient.delete(`/api/admin/pricing/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to delete pricing rule');
};

// Gallery
export const getGalleryImages = async () => {
  const response = await apiClient.get('/api/File/Files', { params: { mediaType: 2 } });
  return response.data.data || [];
};

export const uploadGalleryImages = async (files) => {
  const formData = new FormData();
  formData.append('MediaType', '2');
  files.forEach(file => formData.append('Files', file));
  const response = await apiClient.post('/api/File/Upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to upload images');
  return response.data.data || [];
};

export const deleteGalleryImage = async (fileId) => {
  const response = await apiClient.delete(`/api/File/${fileId}`);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to delete image');
};

// Hero Slider images (MediaType = 1)
export const getHeroSliderImages = async () => {
  const response = await apiClient.get('/api/File/Files', { params: { mediaType: 1 } });
  return response.data.data || [];
};

export const uploadHeroSliderImages = async (files) => {
  const formData = new FormData();
  formData.append('MediaType', '1');
  files.forEach(file => formData.append('Files', file));
  const response = await apiClient.post('/api/File/Upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to upload slider images');
  return response.data.data || [];
};

export const deleteHeroSliderImage = async (fileId) => {
  const response = await apiClient.delete(`/api/File/${fileId}`);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to delete slider image');
};

// Walk-in booking
export const createWalkIn = async (data) => {
  const response = await apiClient.post('/api/Admin/WalkIn', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || response.data.message || 'Walk-in booking failed');
  return response.data.data;
};

// Room availability search (for walk-in room picker)
export const searchAvailableRooms = async (checkIn, checkOut, guests = 1) => {
  const response = await apiClient.post('/api/Search', {
    checkInDate: checkIn,
    checkOutDate: checkOut,
    numberOfGuests: guests,
  });
  return response.data?.availableRooms || response.data?.data?.availableRooms || [];
};

// Reports
export const getReports = async (period = 'monthly', months = 6) => {
  const response = await apiClient.get('/api/Admin/Reports', { params: { period, months } });
  return response.data.data;
};

// Explore Page
export const getExploreContent = async () => {
  const response = await apiClient.get('/api/Explore/GetContent');
  return response.data.data || {};
};

export const saveExploreContent = async (data) => {
  const response = await apiClient.post('/api/Explore/Save', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to save explore content');
  return response.data.data;
};

export const addExploreActivity = async (data) => {
  const response = await apiClient.post('/api/Explore/Activity/Add', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add activity');
  return response.data.data;
};

export const updateExploreActivity = async (id, data) => {
  const response = await apiClient.put(`/api/Explore/Activity/${id}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update activity');
  return response.data.data;
};

export const deleteExploreActivity = async (id) => {
  const response = await apiClient.delete(`/api/Explore/Activity/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to delete activity');
  return response.data.data;
};

export const uploadExploreImages = async (files) => {
  const formData = new FormData();
  formData.append('MediaType', '5');
  files.forEach(file => formData.append('Files', file));
  const response = await apiClient.post('/api/File/Upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to upload images');
  return response.data.data || [];
};

export const uploadExploreActivityImage = async (file) => {
  const formData = new FormData();
  formData.append('MediaType', '5');
  formData.append('Files', file);
  const response = await apiClient.post('/api/File/Upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to upload image');
  const files = response.data.data || [];
  return files[0]?.url || null;
};

export const deleteExploreImage = async (id) => {
  const response = await apiClient.delete(`/api/File/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to delete image');
};

// Staff
export const getStaff = async () => {
  const response = await apiClient.get('/api/Admin/Staff');
  return response.data.data || [];
};

export const addStaff = async (data) => {
  const response = await apiClient.post('/api/Admin/Staff', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add staff member');
  return response.data.data;
};

export const updateStaff = async (id, data) => {
  const response = await apiClient.put(`/api/Admin/Staff/${id}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update staff member');
  return response.data.data;
};

export const resetStaffPassword = async (id, newPassword) => {
  const response = await apiClient.post(`/api/Admin/Staff/${id}/ResetPassword`, { newPassword });
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to reset password');
  return response.data.data;
};

export const deleteStaff = async (id) => {
  const response = await apiClient.delete(`/api/Admin/Staff/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to deactivate staff member');
  return response.data.data;
};

export const addWorkLog = async (staffId, data) => {
  const response = await apiClient.post(`/api/Admin/Staff/${staffId}/WorkLog`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add work log');
  return response.data.data;
};

export const getWorkLogs = async (staffId, year, month) => {
  const response = await apiClient.get(`/api/Admin/Staff/${staffId}/WorkLog`, { params: { year, month } });
  return response.data.data || [];
};

export const updateWorkLog = async (logId, data) => {
  const response = await apiClient.put(`/api/Admin/Staff/WorkLog/${logId}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update work log');
  return response.data.data;
};

export const deleteWorkLog = async (logId) => {
  const response = await apiClient.delete(`/api/Admin/Staff/WorkLog/${logId}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to delete work log');
  return response.data.data;
};

export const getPayroll = async (year, month) => {
  const response = await apiClient.get('/api/Admin/Staff/Payroll', { params: { year, month } });
  return response.data.data || [];
};

// HR — Employees
export const getEmployees = async () => {
  const response = await apiClient.get('/api/Admin/HR/Employees');
  return response.data.data || [];
};

export const addEmployee = async (data) => {
  const response = await apiClient.post('/api/Admin/HR/Employees', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add employee');
  return response.data.data;
};

export const updateEmployee = async (id, data) => {
  const response = await apiClient.put(`/api/Admin/HR/Employees/${id}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update employee');
  return response.data.data;
};

export const deleteEmployee = async (id) => {
  const response = await apiClient.delete(`/api/Admin/HR/Employees/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to deactivate employee');
  return response.data.data;
};

// HR — Attendance
export const getAttendance = async (employeeId, year, month) => {
  const response = await apiClient.get(`/api/Admin/HR/Employees/${employeeId}/Attendance`, { params: { year, month } });
  return response.data.data || [];
};

export const addAttendance = async (employeeId, data) => {
  const response = await apiClient.post(`/api/Admin/HR/Employees/${employeeId}/Attendance`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add attendance');
  return response.data.data;
};

export const updateAttendance = async (id, data) => {
  const response = await apiClient.put(`/api/Admin/HR/Attendance/${id}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update attendance');
  return response.data.data;
};

export const deleteAttendance = async (id) => {
  const response = await apiClient.delete(`/api/Admin/HR/Attendance/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to delete attendance');
  return response.data.data;
};

// Tickets
export const getAdminTicketPage = async () => {
  const response = await apiClient.get('/api/Admin/Ticket/Page');
  return response.data.data || {};
};

export const saveTicketPageSettings = async (data) => {
  const response = await apiClient.post('/api/Admin/Ticket/Page', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to save settings');
  return response.data.data;
};

export const getAdminTicketTypes = async () => {
  const response = await apiClient.get('/api/Admin/Ticket/Types');
  return response.data.data || [];
};

export const addTicketType = async (data) => {
  const response = await apiClient.post('/api/Admin/Ticket/Types', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to add ticket type');
  return response.data.data;
};

export const updateTicketType = async (id, data) => {
  const response = await apiClient.put(`/api/Admin/Ticket/Types/${id}`, data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to update ticket type');
  return response.data.data;
};

export const deleteTicketType = async (id) => {
  const response = await apiClient.delete(`/api/Admin/Ticket/Types/${id}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to delete ticket type');
  return response.data.data;
};

export const getTicketOrders = async (params = {}) => {
  const response = await apiClient.get('/api/Admin/Ticket/Orders', { params });
  return response.data.data || [];
};

export const getTicketOrderDetail = async (id) => {
  const response = await apiClient.get(`/api/Admin/Ticket/Orders/${id}`);
  return response.data.data;
};

export const confirmTicketPayment = async (id) => {
  const response = await apiClient.post(`/api/Admin/Ticket/Orders/${id}/ConfirmPayment`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to confirm payment');
  return response.data.data;
};

export const cancelTicketOrder = async (id) => {
  const response = await apiClient.post(`/api/Admin/Ticket/Orders/${id}/Cancel`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to cancel order');
  return response.data.data;
};

export const checkInTicketOrder = async (id) => {
  const response = await apiClient.post(`/api/Admin/Ticket/Orders/${id}/CheckIn`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to check in');
  return response.data.data;
};

export const getRoomCalendar = async (month) => {
  const response = await apiClient.get('/api/admin/rooms/calendar', { params: { month } });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to load room calendar');
  return response.data.data; // { month, rooms: [{ roomId, roomNumber, roomTypeName, days: [{day, status}] }] }
};

// Manual Payment Settings
export const getPaymentConfig = async () => {
  const response = await apiClient.get('/api/PaymentConfig/Get');
  return response.data.data || { hasConfig: false, paymentMode: null };
};

export const getManualPaymentSettings = async () => {
  const response = await apiClient.get('/api/Admin/ManualPayment');
  return response.data.data || {};
};

export const saveManualPaymentSettings = async (data) => {
  const response = await apiClient.post('/api/Admin/ManualPayment', data);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to save settings');
  return response.data.data;
};

// Room Blocking
export const getPhysicalRooms = async () => {
  const month = new Date().toISOString().substring(0, 7);
  const response = await apiClient.get('/api/admin/rooms/calendar', { params: { month } });
  if (!response.data.success) throw new Error(response.data.message || 'Failed to load rooms');
  return response.data.data?.rooms || [];
};

export const getRoomBlocks = async (roomId) => {
  const response = await apiClient.get(`/api/admin/rooms/${roomId}/blocks`);
  if (!response.data.success) throw new Error(response.data.message || 'Failed to load blocks');
  return response.data.data || [];
};

export const blockRoom = async (roomId, fromDate, toDate, reason) => {
  const response = await apiClient.post(`/api/admin/rooms/${roomId}/block`, { fromDate, toDate, reason: reason || null });
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to block room');
  return response.data.data;
};

export const unblockRoom = async (roomId, blockId) => {
  const response = await apiClient.delete(`/api/admin/rooms/${roomId}/block/${blockId}`);
  if (!response.data.success) throw new Error(response.data.errors?.[0] || 'Failed to unblock room');
  return response.data.data;
};

// Inventory
export const getInventoryItems = async () => {
  const r = await apiClient.get('/api/Admin/Inventory/Items');
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to load items');
  return r.data.data || [];
};

export const addInventoryItem = async (data) => {
  const r = await apiClient.post('/api/Admin/Inventory/Items', data);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to add item');
  return r.data.data;
};

export const updateInventoryItem = async (id, data) => {
  const r = await apiClient.put(`/api/Admin/Inventory/Items/${id}`, data);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to update item');
  return r.data.data;
};

export const deleteInventoryItem = async (id) => {
  const r = await apiClient.delete(`/api/Admin/Inventory/Items/${id}`);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to delete item');
};

export const getInventoryTransactions = async (itemId, limit = 100) => {
  const params = { limit };
  if (itemId) params.itemId = itemId;
  const r = await apiClient.get('/api/Admin/Inventory/Transactions', { params });
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to load transactions');
  return r.data.data || [];
};

export const inventoryPurchase = async (itemId, data) => {
  const r = await apiClient.post(`/api/Admin/Inventory/Items/${itemId}/Purchase`, data);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to record purchase');
  return r.data.data;
};

export const inventoryUsage = async (itemId, data) => {
  const r = await apiClient.post(`/api/Admin/Inventory/Items/${itemId}/Usage`, data);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to record usage');
  return r.data.data;
};

// About Page Content
export const getAboutContent = async () => {
  const r = await apiClient.get('/api/About');
  if (!r.data.success) throw new Error(r.data.message || 'Failed to load about content');
  return r.data.data;
};

export const saveAboutContent = async (data) => {
  const r = await apiClient.put('/api/About', data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to save about content');
  return r.data.data;
};

export const uploadTeamPhoto = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const r = await apiClient.post('/api/About/TeamPhoto', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!r.data.success) throw new Error(r.data.message || 'Failed to upload photo');
  return r.data.data; // returns URL string
};

// Hotel Amenities
export const getHotelAmenities = async () => {
  const r = await apiClient.get('/api/Admin/Amenities');
  if (!r.data.success) throw new Error(r.data.message || 'Failed to load amenities');
  return r.data.data || [];
};

export const addHotelAmenity = async (data) => {
  const r = await apiClient.post('/api/Admin/Amenities', data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to add amenity');
  return r.data.data;
};

export const updateHotelAmenity = async (id, data) => {
  const r = await apiClient.put(`/api/Admin/Amenities/${id}`, data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to update amenity');
  return r.data.data;
};

export const deleteHotelAmenity = async (id) => {
  const r = await apiClient.delete(`/api/Admin/Amenities/${id}`);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to delete amenity');
  return r.data.data;
};

// Nearby Attractions
export const getNearByAttractions = async () => {
  const r = await apiClient.get('/api/Admin/NearByAttractions');
  if (!r.data.success) throw new Error(r.data.message || 'Failed to load attractions');
  return r.data.data || [];
};

export const addNearByAttraction = async (data) => {
  const r = await apiClient.post('/api/Admin/NearByAttractions', data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to add attraction');
  return r.data.data;
};

export const updateNearByAttraction = async (id, data) => {
  const r = await apiClient.put(`/api/Admin/NearByAttractions/${id}`, data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to update attraction');
  return r.data.data;
};

export const deleteNearByAttraction = async (id) => {
  const r = await apiClient.delete(`/api/Admin/NearByAttractions/${id}`);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to delete attraction');
  return r.data.data;
};

// Email Settings (SMTP)
export const getEmailSettings = async () => {
  const r = await apiClient.get('/api/Admin/EmailSettings');
  if (!r.data.success) throw new Error(r.data.message || 'Failed to load email settings');
  return r.data.data;
};

export const saveEmailSettings = async (data) => {
  const r = await apiClient.put('/api/Admin/EmailSettings', data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to save email settings');
  return r.data.data;
};

// Email Templates
export const getEmailTemplates = async () => {
  const r = await apiClient.get('/api/Admin/EmailTemplates');
  if (!r.data.success) throw new Error(r.data.message || 'Failed to load email templates');
  return r.data.data || [];
};

export const saveEmailTemplate = async (templateName, data) => {
  const r = await apiClient.put(`/api/Admin/EmailTemplates/${templateName}`, data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to save template');
  return r.data.data;
};

// Privacy Settings
export const getPrivacySettings = async () => {
  const r = await apiClient.get('/api/Admin/Privacy');
  if (!r.data.success) throw new Error(r.data.message || 'Failed to load privacy settings');
  return r.data.data;
};

export const savePrivacySettings = async (data) => {
  const r = await apiClient.put('/api/Admin/Privacy', data);
  if (!r.data.success) throw new Error(r.data.message || 'Failed to save privacy settings');
  return r.data.data;
};

// Discount Coupons
export const getCoupons = async () => {
  const r = await apiClient.get('/api/Admin/Coupon');
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to load coupons');
  return r.data.data || [];
};

export const createCoupon = async (data) => {
  const r = await apiClient.post('/api/Admin/Coupon', data);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to create coupon');
  return r.data.data;
};

export const toggleCoupon = async (id) => {
  const r = await apiClient.patch(`/api/Admin/Coupon/${id}/Toggle`);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to toggle coupon');
  return r.data.data;
};

export const deleteCoupon = async (id) => {
  const r = await apiClient.delete(`/api/Admin/Coupon/${id}`);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to delete coupon');
  return r.data.data;
};

export const validateCoupon = async (code, bookingTotal) => {
  const r = await apiClient.post('/api/Admin/Coupon/Validate', { code, bookingTotal });
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Coupon validation failed');
  return r.data.data; // VMValidateCouponRsp
};

export const getCouponAnalytics = async (period = 'monthly') => {
  const r = await apiClient.get(`/api/Admin/Coupon/Analytics?period=${period}`);
  if (!r.data.success) throw new Error(r.data.errors?.[0] || 'Failed to load analytics');
  return r.data.data || [];
};
